import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { userService, UpdateUserData } from '../../services/user';
import { formatDocument, validateCPF } from '../../utils/helpers';
import { validateDocument, formatDocument as formatDocumentByType } from '../../utils/documentValidation';
import { validateEmail } from '../../utils/emailValidation';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import { FormButton, FormField } from '../../components/common/Form';
import PhoneInput from '../../components/common/PhoneInput';
import { useForm } from 'react-hook-form';
import { 
  User, 
  Phone, 
  FileText,
  Save,
  Globe
} from 'lucide-react';

interface ProfileFormData {
  name: string;
  email: string;
  nationality?: string;
  document_type?: string;
  document_number?: string;
  date_of_birth?: string;
  phone?: string;
  country_code?: string;
}

const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [emailError, setEmailError] = useState<string>('');

  // Format date for input type="date" (YYYY-MM-DD)
  const formatDateForInput = (dateStr?: string): string => {
    if (!dateStr) return '';
    
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
    
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    watch
  } = useForm<ProfileFormData>({
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      nationality: (user as any)?.nationality || 'BR',
      document_type: (user as any)?.document_type || 'CPF',
      document_number: user?.document_number || '',
      date_of_birth: formatDateForInput(user?.date_of_birth),
      phone: user?.phone || '',
      country_code: user?.country_code || '+55',
    }
  });

  const watchDocumentNumber = watch('document_number');
  const watchDocumentType = watch('document_type');
  const watchNationality = watch('nationality');
  const watchEmail = watch('email');

  // Update form values when user data changes
  React.useEffect(() => {
    if (user) {
      setValue('name', user.name || '');
      setValue('email', user.email || '');
      setValue('nationality', (user as any).nationality || 'BR');
      setValue('document_type', (user as any).document_type || 'CPF');
      setValue('document_number', user.document_number || '');
      setValue('date_of_birth', formatDateForInput(user.date_of_birth));
      setValue('phone', user.phone || '');
      setValue('country_code', user.country_code || '+55');
    }
  }, [user, setValue]);

  // Auto-format document while typing
  React.useEffect(() => {
    if (watchDocumentNumber && watchDocumentType) {
      if (watchDocumentType === 'CPF') {
        const formatted = formatDocument(watchDocumentNumber);
        if (formatted !== watchDocumentNumber) {
          setValue('document_number', formatted);
        }
      }
    }
  }, [watchDocumentNumber, watchDocumentType, setValue]);

  // Email validation
  React.useEffect(() => {
    if (watchEmail && watchEmail.trim()) {
      if (!validateEmail(watchEmail)) {
        setEmailError('Email inválido');
      } else {
        setEmailError('');
      }
    } else {
      setEmailError('');
    }
  }, [watchEmail]);

  const onSubmit = async (data: ProfileFormData) => {
    try {
      setIsLoading(true);
      setMessage(null);

      // Basic validation
      if (!data.name?.trim()) {
        throw new Error('Nome é obrigatório');
      }
      if (!data.email?.trim()) {
        throw new Error('Email é obrigatório');
      }
      
      // Validate email
      if (!validateEmail(data.email)) {
        throw new Error('Email inválido');
      }
      
      // Validate document if provided
      if (data.document_number && data.document_type) {
        if (data.document_type === 'CPF') {
          const cleanCPF = data.document_number.replace(/\D/g, '');
          if (cleanCPF.length === 11 && !validateCPF(cleanCPF)) {
            throw new Error('CPF inválido');
          }
        } else {
          if (!validateDocument(data.document_number, data.document_type)) {
            throw new Error('Documento inválido');
          }
        }
      }

      // Clean document number before sending
      const cleanData: UpdateUserData = {
        name: data.name.trim(),
        email: data.email.trim(),
        nationality: data.nationality || 'BR',
        document_type: data.document_type || 'CPF',
        document_number: data.document_number ? data.document_number.replace(/\D/g, '') : undefined,
        date_of_birth: data.date_of_birth || undefined,
        phone: data.phone?.trim() || undefined,
        country_code: data.country_code || '+55'
      };

      await userService.updateProfile(cleanData);
      await refreshUser();
      
      setMessage({ type: 'success', text: 'Perfil atualizado com sucesso!' });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Erro ao atualizar perfil' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Meu Perfil</h1>
            <p className="text-gray-600">
              Gerencie suas informações pessoais
            </p>
          </div>
        </div>

        {/* Message Display */}
        {message && (
          <div className="mb-6">
            <Alert type={message.type}>
              {message.text}
            </Alert>
          </div>
        )}

        {/* Profile Form */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User className="h-5 w-5 mr-2 text-blue-600" />
                Informações Básicas
              </h2>
              
              <div className="grid grid-cols-1 gap-4">
                <FormField
                  label="Nome Completo"
                  name="name"
                  type="text"
                  placeholder="Seu nome completo"
                  register={register}
                  error={errors.name}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Personal Information */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Globe className="h-5 w-5 mr-2 text-blue-600" />
                Informações Pessoais
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nationality */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nacionalidade
                  </label>
                  <select
                    {...register('nationality')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoading}
                  >
                    <option value="BR">🇧🇷 Brasil</option>
                    <option value="US">🇺🇸 Estados Unidos</option>
                    <option value="GB">🇬🇧 Reino Unido</option>
                    <option value="FR">🇫🇷 França</option>
                    <option value="DE">🇩🇪 Alemanha</option>
                    <option value="ES">🇪🇸 Espanha</option>
                    <option value="IT">🇮🇹 Itália</option>
                    <option value="PT">🇵🇹 Portugal</option>
                    <option value="AR">🇦🇷 Argentina</option>
                    <option value="CL">🇨🇱 Chile</option>
                    <option value="CO">🇨🇴 Colômbia</option>
                    <option value="UY">🇺🇾 Uruguai</option>
                    <option value="PE">🇵🇪 Peru</option>
                    <option value="MX">🇲🇽 México</option>
                    <option value="CA">🇨🇦 Canadá</option>
                    <option value="JP">🇯🇵 Japão</option>
                    <option value="CN">🇨🇳 China</option>
                    <option value="IN">🇮🇳 Índia</option>
                    <option value="AU">🇦🇺 Austrália</option>
                    <option value="OTHER">🌐 Outro</option>
                  </select>
                </div>

                {/* Document Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Documento
                  </label>
                  <select
                    {...register('document_type')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoading}
                  >
                    <option value="CPF">CPF</option>
                    <option value="Passaporte">Passaporte</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Document Information */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                Documentação
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label={watchDocumentType === 'CPF' ? 'CPF' : 'Número do Documento'}
                  name="document_number"
                  type="text"
                  placeholder={watchDocumentType === 'CPF' ? '000.000.000-00' : 'Número do documento'}
                  register={register}
                  error={errors.document_number}
                  disabled={isLoading}
                />

                <FormField
                  label="Data de Nascimento"
                  name="date_of_birth"
                  type="date"
                  register={register}
                  error={errors.date_of_birth}
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Email with validation */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User className="h-5 w-5 mr-2 text-blue-600" />
                Informações de Contato
              </h2>
              
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    {...register('email')}
                    type="email"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      emailError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="seu@email.com"
                    disabled={isLoading}
                  />
                  {emailError && (
                    <p className="mt-1 text-sm text-red-600">{emailError}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Phone className="h-5 w-5 mr-2 text-blue-600" />
                Contato
              </h2>
              
              {/* Phone with Country Code */}
              <PhoneInput
                label="Telefone"
                phoneRegister={register('phone')}
                countryCodeRegister={register('country_code')}
                phoneError={errors.phone}
                countryCodeError={errors.country_code}
                disabled={isLoading}
                placeholder="(85) 99999-9999"
              />
            </div>

            {/* Additional Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">💡 Dica importante</h3>
              <p className="text-sm text-blue-800">
                Manter seus dados atualizados facilita o processo de criação de viagens. 
                Seus dados pessoais podem ser utilizados automaticamente ao criar novas viagens.
              </p>
            </div>

            {/* Account Information */}
            <div className="border-t pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Informações da Conta
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <span className="text-gray-600">Membro desde:</span>
                  <br />
                  <span className="font-medium">
                    {new Date(user.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                
                <div className="bg-gray-50 p-3 rounded-lg">
                  <span className="text-gray-600">Última atualização:</span>
                  <br />
                  <span className="font-medium">
                    {new Date(user.updated_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end pt-6">
              <FormButton
                type="submit"
                variant="primary"
                loading={isLoading}
                disabled={isLoading || !isDirty}
                className="min-w-[140px]"
              >
                <Save className="h-4 w-4 mr-2" />
                Salvar Alterações
              </FormButton>
            </div>
          </form>
        </div>

        {/* Security Note */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            🔒 Suas informações pessoais são protegidas e utilizadas apenas para fins de identificação nas viagens.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
